import { native } from '@forest/native-services'
import { cn } from '@forest/shared'
import { createSignal, For, Show } from 'solid-js'
import heroBgImg from '~/assets/figma/hero-bg.webp'
import { CloseIcon } from '~/components/icon/close'
import { useCountdown } from '~/hooks/use-countdown'
import { language } from '~/lib/params'
import { m } from '~/paraglide/messages'
import { locales } from '~/paraglide/runtime'
import { useTimestamp } from '~/resources/timestamp'

type Props = {
  startDate: Date
}

const pad = (n: number) => String(Math.max(0, n)).padStart(2, '0')

export function CountdownDialog(props: Props) {
  const timestamp = useTimestamp()
  const countdown = useCountdown(() => ({
    now: timestamp(),
    endDate: props.startDate,
  }))
  const isOpen = () => timestamp().getTime() < props.startDate.getTime()
  const [isBgLoaded, setIsBgLoaded] = createSignal(false)

  return (
    <Show when={isOpen()}>
      <div class="fixed inset-0 z-50 overflow-hidden bg-gradient-to-b from-bakery-bg-from from-[54%] to-bakery-bg-to">
        <div
          aria-hidden
          class="absolute inset-x-0 top-0 h-[140px] bg-[#c6a283]"
        />
        <img
          src={heroBgImg}
          alt=""
          aria-hidden="true"
          class={cn(
            'pointer-events-none absolute left-1/2 top-0 h-[500px] w-[1194px] max-w-none -translate-x-1/2 select-none object-cover opacity-0 transition-opacity [-webkit-mask-image:linear-gradient(to_bottom,black_72%,transparent_100%)] [mask-image:linear-gradient(to_bottom,black_72%,transparent_100%)]',
            isBgLoaded() && 'opacity-100',
          )}
          onLoad={() => setIsBgLoaded(true)}
        />

        <header class="pointer-events-none absolute inset-x-0 top-0 z-10 flex flex-col gap-2 pb-3">
          <div
            aria-hidden
            class="relative h-[var(--safe-area-top,0px)] min-h-9 w-full"
          />
          <div class="pointer-events-auto relative flex w-full items-end px-2">
            <button
              type="button"
              onClick={() => native.general.close()}
              class="grid size-[42px] place-items-center overflow-clip rounded-[28px] text-white transition-transform duration-150 active:scale-90"
            >
              <CloseIcon />
            </button>
          </div>
        </header>

        <h1
          class={cn(
            'absolute left-[calc(50%+36px)] top-[calc(158px+var(--safe-area-top))] max-w-[220px] -translate-x-1/2 -translate-y-1/2 whitespace-pre-line pb-3 text-center font-display text-[52px] leading-[1.1]',
            language === 'ja' && 'text-[47px] leading-[0.95]',
          )}
          style={{
            background:
              'radial-gradient(119.07% 75.47% at 60.47% 66.82%, #CC8033 2.71%, #66401A 100%)',
            'background-clip': 'text',
            '-webkit-background-clip': 'text',
            '-webkit-text-fill-color': 'transparent',
          }}
        >
          {m.bakery_intro_hero_title()}
        </h1>

        <div class="absolute inset-x-6 top-[calc(50%+160px)] flex -translate-y-1/2 flex-col items-center gap-[21px]">
          <div class="flex w-[330px] max-w-full flex-col gap-2 text-white">
            <h2
              class="text-[30px] font-bold"
              style={{
                'text-shadow': '0 0 15px rgba(255, 255, 255, 0.70)',
              }}
            >
              {m.dialog_event_not_yet_started_title()}
            </h2>
            <p class="whitespace-pre-line text-base leading-6">
              {m.dialog_event_not_yet_started_description()}
            </p>
          </div>

          <div class="flex w-[330px] max-w-full items-center justify-between">
            <CountdownBox value={pad(countdown().days)} label={m.day()} />
            <span class="text-[25px] text-white">:</span>
            <CountdownBox value={pad(countdown().hours)} label={m.hour()} />
            <span class="text-[25px] text-white">:</span>
            <CountdownBox value={pad(countdown().minutes)} label={m.min()} />
          </div>
        </div>

        <Show when={import.meta.env.DEV}>
          <LocaleDevSwitcher />
        </Show>
      </div>
    </Show>
  )
}

/** Dev-only language switcher overlay — cycle through all Forest locales
 *  to spot which translations overflow the hero title / countdown frame.
 *  Renders only when `import.meta.env.DEV` so it never ships to prod. */
function LocaleDevSwitcher() {
  const currentLang = new URLSearchParams(window.location.search).get(
    'language',
  )
  const switchLocale = (loc: string) => {
    const url = new URL(window.location.href)
    url.searchParams.set('language', loc)
    window.location.href = url.toString()
  }
  return (
    <div class="pointer-events-auto fixed bottom-4 right-4 z-[200] flex max-w-[180px] flex-wrap justify-end gap-1 rounded-lg bg-black/70 p-2 backdrop-blur">
      <For each={locales}>
        {(loc) => (
          <button
            type="button"
            onClick={() => switchLocale(loc)}
            class={cn(
              'rounded px-2 py-1 text-[10px] font-mono leading-none transition-colors',
              currentLang === loc || (currentLang === null && loc === 'en')
                ? 'bg-white text-black'
                : 'bg-white/15 text-white active:bg-white/30',
            )}
          >
            {loc}
          </button>
        )}
      </For>
    </div>
  )
}

function CountdownBox(props: { value: string; label: string }) {
  return (
    <div class="flex size-[88px] flex-col items-center justify-center rounded-[10px] bg-white/30 text-center text-white">
      <span class="text-[36px] font-bold leading-normal">{props.value}</span>
      <span class="text-base">{props.label}</span>
    </div>
  )
}
